import React, { useEffect, useState } from 'react';
import HamburgerMenu from './HamburgerMenu';
import Spinner from './Spinner'; // Ensure this path is correct for your project
import api from './api';

// Map numerical months to their full names
const MONTH_NAMES = {
  "01": "January", "02": "February", "03": "March",
  "04": "April",   "05": "May",      "06": "June",
  "07": "July",    "08": "August",   "09": "September",
  "10": "October", "11": "November", "12": "December"
};

const HistoricalCDRLs = ({ theme }) => {
  const [groupedFiles, setGroupedFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for search and sorting
  const [searchQuery, setSearchQuery] = useState('');
  const [folderSort, setFolderSort] = useState('newest'); // 'newest' or 'oldest'
  const [fileSort, setFileSort] = useState('a-z'); // 'a-z' or 'z-a'
  
  // Track the local theme state, defaulting to whatever the app passes in
  const [isDarkMode, setIsDarkMode] = useState(theme === 'dark');

  // If the app's global theme changes, update this page to match
  useEffect(() => {
    setIsDarkMode(theme === 'dark');
  }, [theme]);

  useEffect(() => {
    async function loadDownloads() {
      try {
        setError(null);
        const response = await api.get('/historical-cdrls/files/');

        if (response.status < 200 || response.status >= 300) {
          throw new Error(`Unexpected response status: ${response.status}`);
        }

        const data = response.data;
        if (!Array.isArray(data)) {
          throw new Error('Unexpected response format from historical CDRLs API.');
        }
        
        const grouped = {};
        
        data.forEach(file => {
          const parts = file.name.split('/');
          
          const year = parts[0];
          if (!year || year === "") return; 
          
          if (!grouped[year]) grouped[year] = {};

          if (parts.length >= 2 && parts[1] !== "") {
            const monthNum = parts[1];
            const monthName = MONTH_NAMES[monthNum] || monthNum;

            if (!grouped[year][monthNum]) {
              grouped[year][monthNum] = {
                displayName: monthName,
                files: []
              };
            }

            if (parts.length >= 3 && !file.name.endsWith('/')) {
              const fileName = parts.slice(2).join('/');
              
              if (fileName && fileName.trim() !== '') {
                grouped[year][monthNum].files.push({
                  ...file,
                  cleanName: fileName
                });
              }
            }
          }
        });

        setGroupedFiles(grouped);
      } catch (err) {
        console.error("Could not load downloads", err);
        const apiError =
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to load historical CDRLs.';
        const status = err?.response?.status;
        setError(status ? `${apiError} (HTTP ${status})` : String(apiError));
        setGroupedFiles({});
      } finally {
        setLoading(false);
      }
    }
    loadDownloads();
  }, []);

  // Define the styles based on our local dark mode toggle
  const currentTheme = isDarkMode ? 'dark' : 'light';
  
  const styles = {
      page: {
          backgroundColor: isDarkMode ? '#121212' : '#ffffff',
          color: isDarkMode ? '#ffffff' : '#000000',
          minHeight: '100vh',
          position: 'relative',
          transition: 'all 0.3s ease'
      },
      container: {
          padding: '20px',
          maxWidth: '1200px',
          margin: '0 auto'
      },
      pageHeader: {
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          marginBottom: '20px',
          borderBottom: isDarkMode ? '1px solid #333' : '1px solid #eee',
          paddingBottom: '10px'
      },
      pageTitle: {
          margin: 0,
          fontSize: '1.8rem',
      },
      folderBorder: isDarkMode ? '#444444' : '#cccccc',
      mutedText: isDarkMode ? '#aaaaaa' : '#666666',
      cardBg: isDarkMode ? '#1e1e1e' : '#f9f9f9',
      cardBorder: isDarkMode ? '#333333' : '#eeeeee',
      buttonBg: isDarkMode ? '#333333' : '#e0e0e0',
      buttonText: isDarkMode ? '#ffffff' : '#000000',
      controlPanel: {
          display: 'flex',
          gap: '15px',
          marginBottom: '25px',
          flexWrap: 'wrap'
      },
      inputStyle: {
          padding: '12px',
          borderRadius: '6px',
          border: `1px solid ${isDarkMode ? '#444444' : '#cccccc'}`,
          backgroundColor: isDarkMode ? '#1e1e1e' : '#f9f9f9',
          color: isDarkMode ? '#ffffff' : '#000000',
          fontSize: '1rem',
          boxSizing: 'border-box'
      },
      errorBanner: {
          padding: '14px 16px',
          marginBottom: '20px',
          borderRadius: '6px',
          border: `1px solid ${isDarkMode ? '#8b3a3a' : '#f5c2c7'}`,
          backgroundColor: isDarkMode ? '#3a1f1f' : '#f8d7da',
          color: isDarkMode ? '#ffb4b4' : '#842029',
      }
  };

  // Pre-sort logic for Years
  let sortedYears = Object.keys(groupedFiles).sort();
  if (folderSort === 'newest') sortedYears.reverse();

  // The Loading View
  if (loading) {
      return (
          <div style={styles.page}>
              <div style={styles.container}>
                  <div style={styles.pageHeader}>
                      <HamburgerMenu theme={currentTheme}/>
                      <h1 style={styles.pageTitle}>Historical CDRL Downloads</h1>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                      <Spinner />
                  </div>
              </div>
          </div>
      );
  }

  // The Loaded Data View
  return (
      <div style={styles.page}>
          
          {/* The Toggle Button */}
          <button 
              onClick={() => setIsDarkMode(!isDarkMode)}
              style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  backgroundColor: styles.buttonBg,
                  color: styles.buttonText,
                  border: 'none',
                  borderRadius: '5px',
                  fontWeight: 'bold',
                  transition: 'all 0.3s ease',
                  zIndex: 10
              }}
          >
              {isDarkMode ? '☀️ Light' : '🌙 Dark'}
          </button>

          <div style={styles.container}>
              
              <div style={styles.pageHeader}>
                  <HamburgerMenu theme={currentTheme}/>
                  <h1 style={styles.pageTitle}>Historical CDRL Downloads</h1>
              </div>

              {error && (
                  <div role="alert" style={styles.errorBanner}>
                      {error}
                  </div>
              )}
              
              {/* Search and Sorting Control Panel */}
              <div style={styles.controlPanel}>
                  <input 
                      type="text" 
                      placeholder="Search for a specific CDRL..." 
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{ ...styles.inputStyle, flex: '1', minWidth: '250px' }}
                  />
                  
                  <select 
                      value={folderSort} 
                      onChange={(e) => setFolderSort(e.target.value)}
                      style={styles.inputStyle}
                  >
                      <option value="newest">Folders: Newest First</option>
                      <option value="oldest">Folders: Oldest First</option>
                  </select>

                  <select 
                      value={fileSort} 
                      onChange={(e) => setFileSort(e.target.value)}
                      style={styles.inputStyle}
                  >
                      <option value="a-z">Files: A to Z</option>
                      <option value="z-a">Files: Z to A</option>
                  </select>
              </div>
              
              <div id="file-list-container">
                  {error ? null : Object.keys(groupedFiles).length === 0 ? (
                  <p>No folders or files found.</p>
                  ) : (
                  sortedYears.map(year => {
                      
                      // Check if this year has any matches before rendering it
                      const yearHasMatches = Object.values(groupedFiles[year]).some(monthData => 
                          monthData.files.some(file => file.cleanName.toLowerCase().includes(searchQuery.toLowerCase()))
                      );

                      if (searchQuery !== '' && !yearHasMatches) return null;

                      // Pre-sort logic for Months
                      let sortedMonths = Object.keys(groupedFiles[year]).sort();
                      if (folderSort === 'newest') sortedMonths.reverse();

                      return (
                          <details key={year} open={searchQuery !== ''} style={{ marginBottom: '15px', border: `1px solid ${styles.folderBorder}`, padding: '10px', borderRadius: '5px' }}>
                          <summary style={{ fontSize: '1.25rem', fontWeight: 'bold', cursor: 'pointer' }}>
                              📁 {year}
                          </summary>
                          
                          <div style={{ marginLeft: '20px', marginTop: '10px' }}>
                              {Object.keys(groupedFiles[year]).length === 0 ? (
                              <p style={{ color: styles.mutedText, fontStyle: 'italic', margin: '5px 0' }}>No months in this year.</p>
                              ) : (
                              sortedMonths.map(monthNum => {
                                  const monthData = groupedFiles[year][monthNum];
                                  
                                  // Filter the files in this month based on the search
                                  const filteredFiles = monthData.files.filter(file => 
                                      file.cleanName.toLowerCase().includes(searchQuery.toLowerCase())
                                  );

                                  // Apply File Sorting (A-Z or Z-A)
                                  filteredFiles.sort((a, b) => {
                                      if (fileSort === 'a-z') return a.cleanName.localeCompare(b.cleanName);
                                      return b.cleanName.localeCompare(a.cleanName);
                                  });

                                  if (searchQuery !== '' && filteredFiles.length === 0) return null;
                                  
                                  return (
                                  <details key={monthNum} open={searchQuery !== ''} style={{ marginBottom: '10px' }}>
                                      <summary style={{ fontSize: '1.1rem', cursor: 'pointer', color: styles.page.color }}>
                                      📂 {monthData.displayName}
                                      </summary>
                                      
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginLeft: '25px', marginTop: '10px' }}>
                                      
                                      {filteredFiles.length === 0 && searchQuery === '' ? (
                                          <p style={{ color: styles.mutedText, fontStyle: 'italic', margin: '5px 0' }}>No files in this folder.</p>
                                      ) : (
                                          filteredFiles.map((file, index) => (
                                              <div key={index} className="file-card" style={{ padding: '10px', border: `1px solid ${styles.cardBorder}`, borderRadius: '6px', backgroundColor: styles.cardBg }}>
                                                  <h4 style={{ margin: '0 0 5px 0', wordBreak: 'break-all' }}>{file.cleanName}</h4>
                                                  <p style={{ margin: '0 0 10px 0', fontSize: '0.85rem', color: styles.mutedText }}>Size: {(file.size / 1024).toFixed(1)} KB</p>
                                                  <a 
                                                  href={file.downloadUrl} 
                                                  download 
                                                  style={{ textDecoration: 'none', backgroundColor: '#007bff', color: 'white', padding: '5px 10px', borderRadius: '4px', fontSize: '0.9rem', display: 'inline-block' }}
                                                  >
                                                  Download File
                                                  </a>
                                              </div>
                                          ))
                                      )}

                                      </div>
                                  </details>
                                  )
                              })
                              )}
                          </div>
                          </details>
                      )
                  })
                  )}
              </div>
          </div>
      </div>
  );
};

export default HistoricalCDRLs;
